import { Component, EventEmitter, OnDestroy, OnInit, Output } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatTableDataSource } from '@angular/material/table';

import { Store } from '@ngrx/store';

import { Observable, Subscription } from 'rxjs';

import { ToastrService } from 'ngx-toastr';

import { AppState } from 'src/app/state';
import { Template } from 'src/app/models/template';
import { Section } from 'src/app/models/section';
import { Subsection } from 'src/app/models/subsection';
import { Bar } from 'src/app/models/bar';
import { CalculatedVariable } from 'src/app/models/calculated-variable';
import { Factor } from 'src/app/models/factor';
import { TemplateConfig } from 'src/app/models/template-config';
import { selectSelectedTemplate } from 'src/app/dashboard/state/templates/templates.selectors';
import { selectSelectedSection } from 'src/app/dashboard/state/sections/sections.selectors';
import { selectSelectedSubsection } from 'src/app/dashboard/state/subsections/subsections.selectors';
import { selectCalculatedVariables } from 'src/app/dashboard/state/calculated-variable/calculated-variable.selectors';
import { saveTemplateConfiguration } from 'src/app/dashboard/state/template-config/template-config.actions';
import {
  selectIsLoadingTemplateConfig,
  selectSelectedTemplateConfig,
  selectTemplateConfigLoaded,
} from 'src/app/dashboard/state/template-config/template-config.selectors';

const BAR_TITLE = 'bar-title';

@Component({
  selector: 'dashboard-dimension-configuration',
  templateUrl: './dimension-configuration.component.html',
  styleUrls: ['./dimension-configuration.component.scss'],
})
export class DimensionConfigurationComponent implements OnInit, OnDestroy {
  @Output() configurationSaved = new EventEmitter();

  factorsForm: FormGroup;

  isLoadingTemplateConfig: Observable<boolean>;
  templateConfigLoaded: Observable<boolean>;

  selectedTemplate: Template;
  selectedSection: Section;
  selectedSubsection: Subsection;
  bars: Bar[];
  availableBars: Bar[];
  calculatedVariables: CalculatedVariable[];
  templateConfig: TemplateConfig;

  factors: Factor[] = [];
  subsectionIndex: number;

  dataSource: MatTableDataSource<Factor>;
  columnsToDisplay = ['bar', 'minValue', 'maxValue', 'minRange', 'maxRange'];

  isEditingFactor = false;
  selectedFactor: Factor;
  selectedFactorIndex: number;

  BAR_TITLE = BAR_TITLE;

  private subscriptions = new Subscription();

  constructor(private formBuilder: FormBuilder, private store: Store<AppState>, private toastrService: ToastrService) {}

  ngOnInit(): void {
    this.factorsForm = this.formBuilder.group({
      barId: [null, Validators.required],
      calculatedVariableId: [null, Validators.required],
      minValue: [null, Validators.required],
      maxValue: [null, Validators.required],
      minRange: [null, Validators.required],
      maxRange: [null, Validators.required],
    });

    this.isLoadingTemplateConfig = this.store.select(selectIsLoadingTemplateConfig);
    this.templateConfigLoaded = this.store.select(selectTemplateConfigLoaded);

    this.subscriptions.add(
      this.store.select(selectSelectedTemplate).subscribe((template) => (this.selectedTemplate = template))
    );
    this.subscriptions.add(
      this.store.select(selectSelectedSection).subscribe((section) => (this.selectedSection = section))
    );
    this.subscriptions.add(
      this.store.select(selectSelectedSubsection).subscribe((subsection) => {
        this.selectedSubsection = subsection;
        this.bars = this.selectedSubsection?.bars?.slice();
        this.subsectionIndex = this.selectedSection?.subsections?.indexOf(this.selectedSubsection);
      })
    );
    this.subscriptions.add(
      this.store
        .select(selectCalculatedVariables)
        .subscribe((calculatedVariables) => (this.calculatedVariables = calculatedVariables))
    );
    this.subscriptions.add(
      this.store.select(selectSelectedTemplateConfig).subscribe((templateConfig) => {
        this.templateConfig = templateConfig;
        this.factors = this.templateConfig?.factors.slice();
        this.availableBars = this.bars?.slice();
        this.factors?.forEach((factor) => {
          const factorBar = this.bars?.find((bar) => bar.id === factor.barId);
          this.availableBars?.splice(this.availableBars.indexOf(factorBar), 1);
        });
        this.dataSource = new MatTableDataSource(this.factors);
      })
    );
  }

  get barId(): AbstractControl {
    return this.factorsForm.get('barId') as AbstractControl;
  }

  get calculatedVariableId(): AbstractControl {
    return this.factorsForm.get('calculatedVariableId') as AbstractControl;
  }

  get minValue(): AbstractControl {
    return this.factorsForm.get('minValue') as AbstractControl;
  }

  get maxValue(): AbstractControl {
    return this.factorsForm.get('maxValue') as AbstractControl;
  }

  get minRange(): AbstractControl {
    return this.factorsForm.get('minRange') as AbstractControl;
  }

  get maxRange(): AbstractControl {
    return this.factorsForm.get('maxRange') as AbstractControl;
  }

  getBarTitle(barId) {
    const selectedBar = this.bars?.filter((bar) => bar.id === barId)[0];
    const filteredTranslations = selectedBar?.translations?.filter(
      (translation) => translation?.identifier === BAR_TITLE
    );
    return filteredTranslations?.length ? filteredTranslations[0].translation : '';
  }

  addFactor() {
    const isFormValid = this.validateForm();
    if (!isFormValid) {
      return;
    }
    const factor = {
      ...this.factorsForm.value,
      feedbackTemplateId: this.selectedTemplate.id,
      interventionId: this.selectedTemplate.interventionId,
      subsectionOrder: this.subsectionIndex,
      subsection: this.selectedSubsection.id,
    };

    this.factors.push(factor);
    this.dataSource = new MatTableDataSource(this.factors);
    this.availableBars = this.availableBars.slice().filter((bar) => bar.id !== factor.barId);
    this.factorsForm.reset();
  }

  validateForm(): boolean {
    this.factorsForm.markAllAsTouched();
    if (this.factorsForm.invalid) {
      return false;
    }

    if (
      this.minRange.value > this.minValue.value ||
      this.minValue.value >= this.maxValue.value ||
      this.maxValue.value > this.maxRange.value
    ) {
      this.toastrService.error('Please check the input values');
      return false;
    }
    return true;
  }

  editFactor(factor: Factor, index: number) {
    if (this.isEditingFactor) {
      return;
    }
    this.selectedFactor = factor;
    this.selectedFactorIndex = index;
    this.isEditingFactor = true;
    this.addToAvailableBars();
  }

  saveEditingFactor() {
    const isFormValid = this.validateForm();
    if (!isFormValid) {
      return;
    }

    const editedFactor = {
      ...this.selectedFactor,
      ...this.factorsForm.value,
    };

    this.factors[this.selectedFactorIndex] = editedFactor;
    this.dataSource = new MatTableDataSource(this.factors);
    this.removeFromAvailableBars();

    this.resetForm();
  }

  deleteFactor() {
    this.factors.splice(this.selectedFactorIndex, 1);
    this.dataSource = new MatTableDataSource(this.factors);
    this.resetForm();
  }

  cancelEditingFactor() {
    this.removeFromAvailableBars();
    this.resetForm();
  }

  addToAvailableBars() {
    const barToAdd = this.bars.filter((bar) => bar.id === this.selectedFactor.barId)[0];
    this.availableBars.push(barToAdd);
  }

  removeFromAvailableBars() {
    this.availableBars = this.availableBars.slice().filter((bar) => bar.id !== this.selectedFactor.barId);
  }

  resetForm() {
    this.isEditingFactor = false;
    this.selectedFactor = null;
    this.selectedFactorIndex = -1;
    this.factorsForm.reset();
  }

  saveConfiguration() {
    const config: TemplateConfig = {
      feedbackTemplateId: this.selectedTemplate.id,
      currentProfile: this.selectedSubsection.id,
      isDimension: true,
      dimensionIndex: this.subsectionIndex,
      factors: this.factors,
      meanFactors: [],
      profiles: [],
    };

    this.store.dispatch(saveTemplateConfiguration({ config, studyId: this.selectedTemplate.studyId }));
    this.configurationSaved.emit(true);
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
